<template>
  <validation-observer
    ref="validationObserver"
    v-slot="{ validate, errors, invalid }"
    slim
  >
    <a-dialog v-model="show" max-width="800px">
      <div
        slot="header"
        v-t="'Config.UserProfile.ProfilePreferences.ChooseUserImage'"
      />
      <div slot="body">
        <div class="row mt-4 mx-4 no-gutters">
          <div class="col">
            <a-form-group
              slot="body"
              :label="$t('Config.UserProfile.ProfilePreferences.UserImage')"
            >
              <validation-provider
                v-slot="{ validate, errors }"
                rules="only-image-types|required"
                vid="input.userImage"
                slim
              >
                <a-file-input
                  id="file"
                  v-model="file"
                  :accept="acceptedImageTypes()"
                  :hint="uploadHelpText()"
                  :error-messages="errors"
                  :placeholder="
                    $t('Config.UserProfile.ProfilePreferences.ChooseFile')
                  "
                  @change="getChoosenImage($event) || validate($event)"
                />
              </validation-provider>
            </a-form-group>
          </div>
        </div>
        <div>
          <Cropper
            ref="cropper"
            classname="cropper"
            :src="image"
            :stencil-props="stencilProps()"
          />
        </div>
      </div>
      <div slot="footer">
        <button
          v-t="'Config.UserProfile.ProfilePreferences.AbortChooseUserImage'"
          type="button"
          class="btn btn-pf-secondary mr-2"
          @click="cancelDialog()"
        />
        <button
          v-t="'Config.UserProfile.ProfilePreferences.AcceptChoosedImage'"
          type="button"
          class="btn btn-pf-primary"
          :disabled="invalid"
          @click="onImageChanged"
        />
      </div>
    </a-dialog>
  </validation-observer>
</template>

<script>
import { Cropper } from "vue-advanced-cropper";
import FormGroup from "+/components/form/formGroup/FormGroup";
import FileInput from "+/components/form/fileInput/FileInput";
import { acceptedImageTypes } from "@/config/init-scripts/validation/utils";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { mapActions } from "vuex";
import Dialog from "+/components/dialog/Dialog";

import {
  getChoosenImage,
  onImageChanged
} from "../utils/profilePreferencesUtils";

export default {
  components: {
    Cropper,
    "a-dialog": Dialog,
    "a-file-input": FileInput,
    "a-form-group": FormGroup,
    "validation-provider": ValidationProvider,
    "validation-observer": ValidationObserver
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    imageUrl: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      loadedImageUrl: undefined,
      file: undefined,
      pictureMaxWidth: undefined,
      pictureRatio: 1
    };
  },
  computed: {
    image() {
      return this.loadedImageUrl ? this.loadedImageUrl : this.imageUrl;
    },
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    }
  },
  created() {
    this.getPersisConfiguration(
      "system.picture.max_width",
      data => (this.pictureMaxWidth = data)
    );
    this.getPersisConfiguration(
      "system.picture.ratio",
      data => (this.pictureRatio = parseFloat(data.replace(",", ".")))
    );
  },
  methods: {
    ...mapActions("userProfile", ["setUserImgSrc", "deleteUserImg"]),
    stencilProps() {
      return { aspectRatio: 1 / this.pictureRatio };
    },
    getChoosenImage(selectedFile) {
      getChoosenImage(
        selectedFile,
        file => (this.loadedImageUrl = file.target.result)
      );
    },
    cancelDialog() {
      this.$emit("input", false);
    },
    acceptedImageTypes() {
      return acceptedImageTypes.join(", ");
    },
    acceptedImageTypesHint() {
      return acceptedImageTypes
        .map(type => type.replace("image/", " *."))
        .sort();
    },
    uploadHelpText() {
      return this.$t(
        "Config.UserProfile.ProfilePreferences.ImageUploadHelpText",
        {
          0: this.acceptedImageTypesHint()
        }
      );
    },
    onImageChanged() {
      this.$refs.validationObserver.validate().then(isValid => {
        if (isValid) {
          const { canvas } = this.$refs.cropper.getResult();
          onImageChanged(
            canvas,
            this.pictureMaxWidth,
            this.pictureRatio,
            this.submit
          );
        }
      });
    },
    submit(imageBlob) {
      if (imageBlob) {
        this.setUserImgSrc(URL.createObjectURL(imageBlob));
        this.$rest.config
          .saveUserImage(imageBlob)
          .then(this.saveSuccess)
          .catch(this.saveError);
      } else {
        const noImageSelectedError = this.$t(
          "Config.UserProfile.ProfilePreferences.NoImageSelectedError"
        );
        this.$feedback(noImageSelectedError, {
          type: "error"
        });
      }
    },
    saveSuccess() {
      const successTranslation = this.$t(
        "Config.UserProfile.ProfilePreferences.ImageSaveSuccess"
      );
      this.$feedback(successTranslation, {
        type: "success"
      });
      this.cancelDialog();
    },
    saveError(error) {
      this.cancelDialog();
      const errorTranslation = this.$t(
        "Config.UserProfile.ProfilePreferences.ImageSaveError"
      );
      this.$feedback(errorTranslation, {
        type: "error"
      });
      window.atomlog(error, "error");
    },
    getPersisConfiguration(configName, callback) {
      this.$rest.config
        .getPersisConfiguration(configName)
        .then(response => callback(response.data))
        .catch(error =>
          window.atomlog(`getPersisConfiguration: ${error}`, "error")
        );
    }
  }
};
</script>

<style lang="scss">
.cropper {
  height: 200px;
  width: 200px;
  background: #ddd;
  margin: 3em;
  margin-left: auto;
  margin-right: auto;
}
</style>
